import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User } from '../types';
import { loginUser, logoutUser } from '../services';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user data for demo purposes
// const mockUser: User = {
//   id: '1',
//   email: 'admin@dawaak.com',
//   name: {
//     en: 'Admin User',
//     ar: 'مستخدم إداري'
//   },
//   role: 'admin',
//   avatar: '/api/placeholder/40/40',
//   createdAt: new Date('2024-01-01'),
//   lastLogin: new Date()
// };


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored authentication on app start
    const checkStoredAuth = () => {
      const storedUser = localStorage.getItem('dawaak_user');
      const sessionUser = sessionStorage.getItem('dawaak_user');

      if (storedUser || sessionUser) {
        try {
          const userData = JSON.parse(storedUser || sessionUser || '');
          setUser(userData);
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem('dawaak_user');
          sessionStorage.removeItem('dawaak_user');
          localStorage.removeItem('dawaak_token');
          sessionStorage.removeItem('dawaak_token');
        }
      }

      setIsLoading(false);
    };

    checkStoredAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe = false): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Make API call using centralized auth service
      const data = await loginUser(email, password);

      console.log('Login response:', data);

      // Assuming the API returns user data and token
      // Adjust this based on your actual API response structure
      const userData: User = {
        id: data.id || data.user?.id || '1',
        email: email,
        name: {
          en: data.name || data.user?.name || 'Admin User',
          ar: data.nameAr || data.user?.nameAr || 'مستخدم إداري'
        },
        role: (data.role || data.user?.role || 'admin') as 'admin' | 'editor',
        avatar: data.avatar || data.user?.avatar || '/api/placeholder/40/40',
        createdAt: new Date(data.createdAt || data.user?.createdAt || Date.now()),
        lastLogin: new Date()
      };

      // Store token if available
      if (data.token) {
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('dawaak_token', data.token);
      }

      setUser(userData);

      // Store user data based on "Remember me" preference
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('dawaak_user', JSON.stringify(userData));

      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Optionally call logout API endpoint
      await logoutUser();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    }

    // Clear local state and storage
    setUser(null);
    localStorage.removeItem('dawaak_user');
    sessionStorage.removeItem('dawaak_user');
    localStorage.removeItem('dawaak_token');
    sessionStorage.removeItem('dawaak_token');
    // Also clear any old keys that might exist
    localStorage.removeItem('dawaak_user_x');
    sessionStorage.removeItem('dawaak_user_x');
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};